import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import { buildExportZip, type ExportData } from "./export";
import { downloadBlob } from "@/lib/download";
import { dateShort, dateTime, money } from "@/lib/format";
import { PageHeader } from "@/components/shared/PageHeader";
import { QueryError } from "@/components/shared/QueryError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const iso = (d: Date) => d.toISOString().slice(0, 10);
const startOfMonth = (offset = 0) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + offset); return iso(d); };
const endOfMonth = (offset = 0) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + offset + 1); d.setDate(0); return iso(d); };

export default function AccountingPage() {
  const qc = useQueryClient();
  const [from, setFrom] = useState(startOfMonth(-1));
  const [to, setTo] = useState(endOfMonth(-1));
  const { data: history, error } = useQuery({
    queryKey: ["accounting_exports"],
    queryFn: async () => { const { data, error } = await supabase.from("accounting_exports").select("*, profiles!accounting_exports_created_by_profile_fk(full_name, email)").order("created_at", { ascending: false }).limit(20); if (error) throw error; return data as unknown as { id: string; period_from: string; period_to: string; rows_count: number; totals: Record<string, unknown>; filename: string | null; created_at: string; profiles: { full_name: string | null; email: string } | null }[]; },
  });
  const { data: preview, isFetching } = useQuery({
    queryKey: ["accounting_preview", from, to],
    queryFn: async () => { const { data, error } = await supabase.rpc("accounting_export", { _from: from, _to: to }); if (error) throw error; return data as unknown as ExportData; },
    enabled: Boolean(from && to && from <= to),
  });

  const run = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc("accounting_export", { _from: from, _to: to }); if (error) throw error;
      const d = data as unknown as ExportData;
      const { blob, filename, rows } = buildExportZip(d);
      downloadBlob(blob, filename);
      const { error: e2 } = await supabase.rpc("accounting_export_log", { _from: from, _to: to, _rows: rows, _totals: d.summary as never, _filename: filename });
      if (e2) throw e2;
      return { rows, filename };
    },
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ["accounting_exports"] }); toast.success(`Export téléchargé : ${r.rows} ligne${r.rows > 1 ? "s" : ""}`); },
    onError: (e) => { toast.error((e as { message?: string }).message ?? "Export impossible."); logger.error("accounting.export", e); },
  });

  const presets: [string, string, string][] = [
    ["Mois en cours", startOfMonth(0), endOfMonth(0)],
    ["Mois précédent", startOfMonth(-1), endOfMonth(-1)],
    ["Trimestre en cours", startOfMonth(-((new Date().getMonth()) % 3)), endOfMonth(0)],
    ["Année en cours", `${new Date().getFullYear()}-01-01`, endOfMonth(0)],
  ];
  const sums = preview?.summary;
  const line = (m: Record<string, number> | undefined) => Object.entries(m ?? {}).map(([c, v]) => money(v, c as "EUR" | "USD")).join(" · ") || "—";

  return (
    <div>
      <PageHeader title="Comptabilité" description="Export d'une période pour ton comptable : factures, avoirs, paiements, leads vendus et banque, en CSV lisibles par Excel. Rien ne quitte le CRM sans que tu cliques." />
      <section className="glass mb-4 rounded-2xl p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5"><Label htmlFor="ex-from">Du</Label><Input id="ex-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-44" /></div>
          <div className="space-y-1.5"><Label htmlFor="ex-to">Au</Label><Input id="ex-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-44" /></div>
          <div className="flex flex-wrap gap-2">{presets.map(([label, f, t]) => <Button key={label} size="sm" variant="outline" onClick={() => { setFrom(f); setTo(t); }}>{label}</Button>)}</div>
          <Button className="ml-auto" disabled={!from || !to || from > to || run.isPending} onClick={() => run.mutate()}><Download className="mr-2 h-4 w-4" />{run.isPending ? "Préparation…" : "Télécharger l'export (ZIP)"}</Button>
        </div>
        {from > to && <p className="mt-2 text-sm text-destructive">La date de fin doit suivre la date de début.</p>}
      </section>

      <section className="glass mb-4 rounded-2xl p-5" data-testid="export-preview">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Contenu de la période {dateShort(from)} → {dateShort(to)}</h3>
        {isFetching && !preview ? <p className="text-sm text-muted-foreground">Calcul…</p> : preview ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Factures" value={preview.invoices.length} hint={line(sums?.invoiced)} />
            <Stat label="Avoirs" value={preview.credit_notes.length} hint={line(sums?.credited)} />
            <Stat label="Paiements" value={preview.payments.length} hint={line(sums?.collected)} />
            <Stat label="Leads vendus" value={preview.leads.length} hint={`${preview.bank.length} ligne${preview.bank.length > 1 ? "s" : ""} bancaire${preview.bank.length > 1 ? "s" : ""}`} />
          </div>
        ) : null}
        <p className="mt-3 text-xs text-muted-foreground">Le ZIP contient aussi un LISEZ-MOI (période, définitions des colonnes) et un récapitulatif par devise. Les montants ne sont jamais convertis.</p>
      </section>

      <section className="glass rounded-2xl p-5">
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground"><FileSpreadsheet className="h-4 w-4" />Exports déjà transmis</h3>
        {error ? <QueryError error={error} /> : !history?.length ? <p className="text-sm text-muted-foreground">Aucun export pour l'instant.</p> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Période</TableHead><TableHead>Lignes</TableHead><TableHead>Fichier</TableHead><TableHead>Par</TableHead><TableHead>Le</TableHead></TableRow></TableHeader>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell>{dateShort(h.period_from)} → {dateShort(h.period_to)}</TableCell>
                    <TableCell>{h.rows_count}</TableCell>
                    <TableCell className="font-mono text-xs">{h.filename ?? "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{h.profiles?.full_name ?? h.profiles?.email ?? "—"}</TableCell>
                    <TableCell>{dateTime(h.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="rounded-xl bg-muted/60 p-4">
      <div className="font-heading text-2xl font-bold">{value}</div>
      <div className="text-sm">{label}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}
